/**
 * HelpDrawer — slide-out help panel. Mirrors Bug Tracker's HelpDrawer.vue.
 * Bound to a single help system (artist or gallery); the launcher decides which.
 */
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { HelpSystemBase } from '../../help-system-base';
import { HelpContentComponent } from '../help-content/help-content.component';

@Component({
  selector: 'app-help-drawer',
  standalone: true,
  imports: [CommonModule, HelpContentComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (system.isOpen$ | async) {
      <div class="help-drawer__backdrop" (click)="system.close()"></div>
      <aside class="help-drawer" [class.help-drawer--gallery]="system.systemId === 'gallery'"
             role="dialog" aria-label="Help">
        <header class="help-drawer__head">
          <div class="help-drawer__nav">
            @if (system.canGoBack$ | async) {
              <button type="button" class="help-drawer__icon" (click)="system.goBack()" aria-label="Back">‹</button>
            }
            <button type="button" class="help-drawer__icon" (click)="system.goToIndex()" aria-label="Help home">⌂</button>
          </div>
          <span class="help-drawer__badge">{{ system.systemId === 'gallery' ? 'Gallery Help' : 'Artist Help' }}</span>
          <button type="button" class="help-drawer__icon help-drawer__close" (click)="system.close()" aria-label="Close">×</button>
        </header>

        <h2 class="help-drawer__title">{{ system.title$ | async }}</h2>

        <div class="help-drawer__scroll">
          <app-help-content [system]="system"></app-help-content>
        </div>
      </aside>
    }
  `,
  styles: [`
    .help-drawer__backdrop { position: fixed; inset: 0; background: rgba(17,24,39,0.35); z-index: 10800; }
    .help-drawer {
      position: fixed; top: 0; right: 0; bottom: 0; width: 420px; max-width: 92vw;
      background: #fff; box-shadow: -8px 0 28px rgba(0,0,0,0.18); z-index: 10801;
      display: flex; flex-direction: column; animation: help-slide-in 0.18s ease-out;
      border-left: 4px solid #6366f1;
    }
    .help-drawer--gallery { border-left-color: #0ea5e9; }
    @keyframes help-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
    .help-drawer__head { display: flex; align-items: center; gap: 0.5rem; padding: 0.6rem 0.8rem; border-bottom: 1px solid #eef0f3; }
    .help-drawer__nav { display: flex; gap: 0.25rem; }
    .help-drawer__badge { flex: 1; text-align: center; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #6366f1; }
    .help-drawer--gallery .help-drawer__badge { color: #0ea5e9; }
    .help-drawer__icon {
      width: 28px; height: 28px; border: none; background: #f3f4f6; border-radius: 6px;
      font-size: 1.1rem; line-height: 1; cursor: pointer; color: #374151;
    }
    .help-drawer__icon:hover { background: #e5e7eb; }
    .help-drawer__close { font-size: 1.3rem; }
    .help-drawer__title { margin: 0; padding: 0.8rem 1rem 0.4rem; font-size: 1.25rem; color: #111827; }
    .help-drawer__scroll { flex: 1; overflow-y: auto; padding: 0 1rem 1.5rem; }
  `],
})
export class HelpDrawerComponent {
  @Input({ required: true }) system!: HelpSystemBase;
}
